export enum Role {
  ADMIN = 'ADMIN',
  OPERATOR = 'OPERATOR',
  VIEWER = 'VIEWER',
}

export enum OrgType {
  PLATFORM = 'PLATFORM',
  TENANT = 'TENANT',
  CUSTOMER = 'CUSTOMER',
}

/** Composite identity: `${OrgType}_${Role}` */
export enum OrgRole {
  PLATFORM_ADMIN = 'PLATFORM_ADMIN',
  PLATFORM_OPERATOR = 'PLATFORM_OPERATOR',
  PLATFORM_VIEWER = 'PLATFORM_VIEWER',
  TENANT_ADMIN = 'TENANT_ADMIN',
  TENANT_OPERATOR = 'TENANT_OPERATOR',
  TENANT_VIEWER = 'TENANT_VIEWER',
  CUSTOMER_ADMIN = 'CUSTOMER_ADMIN',
  CUSTOMER_OPERATOR = 'CUSTOMER_OPERATOR',
  CUSTOMER_VIEWER = 'CUSTOMER_VIEWER',
}

/** Valid role assignments per organization type */
export const ORG_TYPE_ROLES: Record<OrgType, readonly Role[]> = {
  [OrgType.PLATFORM]: [Role.ADMIN, Role.OPERATOR, Role.VIEWER],
  [OrgType.TENANT]: [Role.ADMIN, Role.OPERATOR, Role.VIEWER],
  [OrgType.CUSTOMER]: [Role.ADMIN, Role.OPERATOR, Role.VIEWER],
};

/** All valid org+role combinations, generated from ORG_TYPE_ROLES */
export const ORG_ROLE_COMBINATIONS: readonly OrgRole[] = (
  Object.entries(ORG_TYPE_ROLES) as [OrgType, readonly Role[]][]
).flatMap(([orgType, roles]) => roles.map((role) => toOrgRole(orgType, role)));

const ROLE_LABELS: Record<Role, string> = {
  [Role.ADMIN]: 'Admin',
  [Role.OPERATOR]: 'Operator',
  [Role.VIEWER]: 'Read Only',
};

export const RoleBadgeVariant: Record<Role, 'default' | 'outline' | 'secondary'> = {
  [Role.ADMIN]: 'default',
  [Role.OPERATOR]: 'outline',
  [Role.VIEWER]: 'secondary',
}

export function getRoleBadgeVariant(role: Role): 'default' | 'outline' | 'secondary' {
  return RoleBadgeVariant[role];
}

export function getRoleName(role: Role): string {
  return ROLE_LABELS[role] ?? 'Unknown Role';
}

export function isOrgRole(value: string): value is OrgRole {
  return Object.values(OrgRole).includes(value as OrgRole);
}

export function toOrgRole(orgType: OrgType, role: Role): OrgRole {
  const candidate = `${orgType}_${role}`;
  if (!isOrgRole(candidate)) {
    throw new Error(`Invalid org/role combination: ${orgType}/${role}`);
  }
  if (!ORG_TYPE_ROLES[orgType].includes(role)) {
    throw new Error(`Role ${role} is not allowed for org type ${orgType}`);
  }
  return candidate;
}

export function parseOrgRole(value: string | null | undefined): OrgRole | null {
  if (!value || !isOrgRole(value)) return null;
  return value;
}

export function getShortOrgRoleName(orgRole: OrgRole): string {
  const separatorIndex = orgRole.indexOf('_');
  const role = orgRole.slice(separatorIndex + 1) as Role;
  return getRoleName(role);
}